import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { Tooltip } from "react-tooltip";
import { useNavigate } from "react-router-dom";
import { getModulePermissions } from "../../utils/permissionUtils";

const UnitCountOne = ({ dashboardData, signupRange, setSignupRange, loading, setLoading }) => {
  const userPermissions = getModulePermissions("User");
  const compatibilityPermissions = getModulePermissions("Compatibility Control");
  const matchPermissions = getModulePermissions("Match Control");
  const effectiveCompatibilityPermissions = {
    ...compatibilityPermissions,
    canView: compatibilityPermissions.canView || matchPermissions.canView,
  };
  const vaultPermissions = getModulePermissions("vault-questions");
  const navigate = useNavigate();

  // const cards = [
  //   {
  //     title: "Total Users",
  //     value: dashboardData?.totalUsers || 535,
  //     description: "This shows the total number of users in the system.",
  //     icon: "gridicons:multiple-users",
  //     bg: "bg-warning",
  //     gradient: "bg-gradient-start-5"
  //   },
  //   {
  //     title: "Total Active Users",
  //     value: dashboardData?.activeUsers || 291,
  //     description: "Users who are currently active.",
  //     icon: "gridicons:multiple-users",
  //     bg: "bg-purple",
  //     gradient: "bg-gradient-start-2"
  //   },
  //   {
  //     title: "Total New Sign-up Users",
  //     value: dashboardData?.newUsers || 244,
  //     description: "Users who signed up recently.",
  //     icon: "gridicons:multiple-users",
  //     bg: "bg-cyan",
  //     gradient: "bg-gradient-start-3"
  //   },
  //   {
  //     title: "Total Revenue",
  //     value: dashboardData?.revenue || "$0",
  //     description: "Total revenue generated so far.",
  //     icon: "mdi:wallet",
  //     bg: "bg-purple",
  //     gradient: "bg-gradient-start-2"
  //   },
  //   {
  //     title: "Total System Health",
  //     value: dashboardData?.systemHealth || "100%",
  //     description: "System health status.",
  //     icon: "mdi:monitor",
  //     bg: "bg-warning",
  //     gradient: "bg-gradient-start-3"
  //   },
  //   {
  //     title: "Total Top User Action",
  //     value: dashboardData?.topUser || 0,
  //     description: "Most common user activities in the system.",
  //     icon: "gridicons:multiple-users",
  //     bg: "bg-success-main",
  //     gradient: "bg-gradient-start-4"
  //   },
  //   {
  //     title: "Total Retention Rate",
  //     value: dashboardData?.retentionRate || 0,
  //     description: "How many new users return after 7/30 days?",
  //     icon: "gridicons:multiple-users",
  //     bg: "bg-success-main",
  //     gradient: "bg-gradient-start-4"
  //   },
  //   {
  //     title: "Total Churn Rate",
  //     value: dashboardData?.churnRate || 0,
  //     description: "Users who stop being active.",
  //     icon: "gridicons:multiple-users",
  //     bg: "bg-cyan",
  //     gradient: "bg-gradient-start-1"
  //   },
  //   {
  //     title: "Total Pending Actions",
  //     value: dashboardData?.pendingActions || 0,
  //     description: "Number of flagged accounts, pending approvals, support tickets.",
  //     icon: "gridicons:multiple-users",
  //     bg: "bg-warning-main",
  //     gradient: "bg-gradient-start-5"
  //   }
  // ];

  const cards = [
    {
      title: "Total Users",
      value: dashboardData?.totalUsers || 0,
      // Founder 2026-10-20: say what is counted, and where the old "Active"
      // number went (accounts not suspended or banned).
      description:
        `Real members with an account today. Deleted and test accounts are left out.` +
        (typeof dashboardData?.activeUsers === "number"
          ? ` ${dashboardData.activeUsers} of them are in good standing (not suspended or banned).`
          : ""),
      icon: "gridicons:multiple-users",
      bg: "bg-warning",
      gradient: "bg-gradient-start-5",
      path: "/user",
      module: "User",
    },
    {
      // Was "Active Users", which counted account status, not use of the app
      // (431 of 434 read as active while 140 had opened it in a week).
      title: "Opened the App (7 Days)",
      value: dashboardData?.openedLast7Days ?? 0,
      description: "Real members who opened the app in the last 7 days. Deleted and test accounts are left out.",
      icon: "gridicons:multiple-users",
      bg: "bg-purple",
      gradient: "bg-gradient-start-2",
      // No click-through: the user list has no "opened in 7 days" filter
      // yet, and an unfiltered list would show a different number.
      path: null,
      module: "User",
    },
    {
      title: `New Sign-ups (Last ${signupRange} Days)`,
      value: dashboardData?.newSignUps || 0,
      // description: `Users who signed up during the last ${signupRange} days.`,
      description: `Users who signed up during the last ${signupRange} day${signupRange > 1 ? 's' : ''}.`,
      icon: "gridicons:multiple-users",
      bg: "bg-cyan",
      gradient: "bg-gradient-start-3",
      path: "/user",
      module: "User",
    },
    {
      title: "Total Compatibilities",
      value: dashboardData?.totalCompatibilities || 0,
      description: "Total compatibility records in the system.",
      icon: "mdi:account-multiple",
      bg: "bg-success-main",
      gradient: "bg-gradient-start-4",
      path: "/compatibility-control",
      module: "Compatibility Control",
    },
    {
      // "matched" is a legacy status the blind model never sets, so this
      // card read 1 out of 17 pairs. Revealed is the real milestone
      // (founder 2026-08-14).
      title: "Revealed Compatibilities",
      value: dashboardData?.revealedCompatibilities ?? dashboardData?.matchedCompatibilities ?? 0,
      description: "Pairs where both people chose to reveal.",
      icon: "mdi:handshake",
      bg: "bg-warning",
      gradient: "bg-gradient-start-3",
      path: "/compatibility-control",
      module: "Compatibility Control",
    },
    {
      title: "Total Vault Questions",
      value: dashboardData?.totalVaultQuestions || 0,
      description: "Total Vault questions available in the system.",
      icon: "mdi:clipboard-text",
      bg: "bg-success-main",
      gradient: "bg-gradient-start-4",
      path: "/vault-questions",
      module: "vault-questions",
    },
    {
      // Renamed from "AI Review Vault Questions" (founder 2026-08-14): the
      // number is questions the AI has ALREADY approved, but the old label
      // + red alert icon read as a 163-item backlog needing attention.
      title: "AI Approved Vault Questions",
      value: dashboardData?.aiRevivedVaultQuestions || 0,
      description: "Vault questions the AI has approved.",
      icon: "mdi:robot",
      bg: "bg-success-main",
      gradient: "bg-gradient-start-4",
      path: "/vault-questions",
      module: "vault-questions",
    },
    {
      // The actionable one: what is genuinely waiting on a human.
      title: "Pending Review Vault Questions",
      value: dashboardData?.pendingReviewVaultQuestions || 0,
      description: "Vault questions still waiting for review.",
      icon: "mdi:alert-circle",
      bg: "bg-danger-main",
      gradient: "bg-gradient-start-1",
      path: "/vault-questions",
      module: "vault-questions",
    },

    {
      title: "Published Vault Questions",
      value: dashboardData?.publishedVaultQuestions || 0,
      description: "Vault questions that are published and active.",
      icon: "mdi:check-circle",
      bg: "bg-cyan",
      gradient: "bg-gradient-start-2",
      path: "/vault-questions",
      module: "vault-questions",
    },
  ];

  // const handleCardClick = (path) => {
  //   if (path) navigate(path);
  // };

  const handleCardClick = (card) => {
    if (!card.path) return;

    let query = "";

    // else if (card.title === "New Sign-ups") {
    //   const today = new Date();
    //   const last7Days = new Date();
    //   last7Days.setDate(today.getDate() - 7);

    //   query = `?startDate=${last7Days.toISOString()}&endDate=${today.toISOString()}`;
    // }
    if (card.title.startsWith("New Sign-ups")) {
      const today = new Date();
      const start = new Date();
      start.setDate(today.getDate() - parseInt(signupRange));

      query = `?startDate=${start.toISOString()}&endDate=${today.toISOString()}`;
    } else if (card.title === "Revealed Compatibilities") {
      query = "?status=revealed";
    } else if (card.title === "AI Approved Vault Questions") {
      query = "?status=ai_approved";
    } else if (card.title === "Pending Review Vault Questions") {
      query = "?status=pending_review";
    } else if (card.title === "Published Vault Questions") {
      query = "?isPublished=true"; // Only published
    }

    navigate(`${card.path}${query}`);
  };

  const permissionsMap = {
    User: userPermissions.canView,
    "Compatibility Control": effectiveCompatibilityPermissions.canView,
    "vault-questions": vaultPermissions.canView,
  };
  const filteredCards = cards.filter((card) => permissionsMap[card.module]);

  return (
    <>
      {/* Dropdown for changing new sign-up range */}
      {userPermissions.canView && (
      <div className="mb-4 d-flex justify-content-end align-items-center gap-2">
        <label htmlFor="signupRange" className="fw-medium">
          Show new sign-ups for:
        </label>
        <select
          className="form-select form-select-sm w-auto ps-12 py-6"
          style={{
            fontSize: "16px",
            borderRadius: "7px",
            backgroundPositionX: "unset",
            backgroundPosition: "right 0.75rem center",
          }}
          id="signupRange"
          value={signupRange}
          onChange={(e) => setSignupRange(e.target.value)}
        >
          <option value="1">Today</option>
          <option value="7">Last 7 Days</option>
          <option value="30">Last 30 Days</option>
        </select>
      </div>
      )}
      <div className="row row-cols-xxxl-7 row-cols-lg-3 row-cols-sm-2 row-cols-1 gy-4 mb-10">
        {filteredCards.map((card, idx) => (
          <div className="col" key={idx}>
            <div
              className={`card shadow-none border ${card.gradient} h-100 position-relative`}
              style={{ cursor: card.path ? "pointer" : "default" }}
              onClick={() => handleCardClick(card)}
            >
              <div className="card-body p-20 position-relative">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div>
                    <p className="fw-medium text-primary-light mb-1">
                      {card.title}
                    </p>
                    <h6 className="mb-0">{card.value}</h6>
                  </div>

                  <div
                    className={`w-50-px h-50-px ${card.bg} rounded-circle d-flex justify-content-center align-items-center`}
                  >
                    <Icon
                      icon={card.icon}
                      className="text-white text-2xl mb-0"
                    />
                  </div>
                </div>

                {/* Bottom-right tooltip icon */}
                <span
                  className={`position-absolute ${card.bg} rounded-circle d-flex justify-content-center align-items-center`}
                  style={{
                    bottom: "8px",
                    right: "8px",
                    cursor: "pointer",
                  }}
                  data-tooltip-id={`tooltip-${idx}`}
                  aria-label={`Information about ${card.title}`}
                >
                  <Icon
                    icon="ic:baseline-info"
                    className="text-white"
                    style={{ fontSize: "1rem" }}
                  />
                </span>
                <Tooltip
                  id={`tooltip-${idx}`}
                  place="top"
                  style={{ zIndex: "1" }}
                >
                  {card.description}
                </Tooltip>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default UnitCountOne;
